import type {
  InjectedData,
  SceneAnimationEntry,
  SceneBehaviorCatalog,
} from "../../layout/types.js";
import { normalizeAnimationEasingValue } from "../method.js";
import type {
  AnimatableProperty,
  BehaviorConnection,
  BehaviorNode,
} from "../types.js";
import {
  ANIMATABLE_PROPERTIES,
} from "../types.js";
import { animKeyframeHasPropertyPort } from "../anim-keyframe-ports.js";
import { BehaviorRuntime } from "./BehaviorRuntime.js";
import type { BehaviorAnimationPayload, ExecutionContext } from "./types.js";
import {
  coerceCssAnimationDirection,
  coerceCssAnimationFillMode,
  toFiniteNumber,
} from "./helpers.js";

export interface SceneAnimationExtraction {
  byName: Record<string, BehaviorAnimationPayload>;
  validNames: string[];
}

export const DEFAULT_SCENE_ANIMATIONS: readonly BehaviorAnimationPayload[] = [
  {
    name: "fade",
    durationMs: 700,
    delayMs: 0,
    iterationCount: 1,
    direction: "normal",
    fillMode: "both",
    keyframes: [
      { offset: 0, easing: "ease-in-out", values: { opacity: 0 } },
      { offset: 100, easing: "ease-in-out", values: { opacity: 1 } },
    ],
  },
  {
    name: "slide-up",
    durationMs: 700,
    delayMs: 0,
    iterationCount: 1,
    direction: "normal",
    fillMode: "both",
    keyframes: [
      { offset: 0, easing: "ease-in-out", values: { y: 22, opacity: 0 } },
      { offset: 100, easing: "ease-in-out", values: { y: 0, opacity: 1 } },
    ],
  },
  {
    name: "slide-down",
    durationMs: 700,
    delayMs: 0,
    iterationCount: 1,
    direction: "normal",
    fillMode: "both",
    keyframes: [
      { offset: 0, easing: "ease-in-out", values: { y: -22, opacity: 0 } },
      { offset: 100, easing: "ease-in-out", values: { y: 0, opacity: 1 } },
    ],
  },
  {
    name: "slide-left",
    durationMs: 700,
    delayMs: 0,
    iterationCount: 1,
    direction: "normal",
    fillMode: "both",
    keyframes: [
      { offset: 0, easing: "ease-in-out", values: { x: 22, opacity: 0 } },
      { offset: 100, easing: "ease-in-out", values: { x: 0, opacity: 1 } },
    ],
  },
  {
    name: "slide-right",
    durationMs: 700,
    delayMs: 0,
    iterationCount: 1,
    direction: "normal",
    fillMode: "both",
    keyframes: [
      { offset: 0, easing: "ease-in-out", values: { x: -22, opacity: 0 } },
      { offset: 100, easing: "ease-in-out", values: { x: 0, opacity: 1 } },
    ],
  },
  {
    name: "zoom-in",
    durationMs: 700,
    delayMs: 0,
    iterationCount: 1,
    direction: "normal",
    fillMode: "both",
    keyframes: [
      { offset: 0, easing: "ease-in-out", values: { scale: 0.86, opacity: 0 } },
      { offset: 100, easing: "ease-in-out", values: { scale: 1, opacity: 1 } },
    ],
  },
  {
    name: "zoom-out",
    durationMs: 700,
    delayMs: 0,
    iterationCount: 1,
    direction: "normal",
    fillMode: "both",
    keyframes: [
      { offset: 0, easing: "ease-in-out", values: { scale: 1.14, opacity: 0 } },
      { offset: 100, easing: "ease-in-out", values: { scale: 1, opacity: 1 } },
    ],
  },
  {
    name: "pop",
    durationMs: 700,
    delayMs: 0,
    iterationCount: 1,
    direction: "normal",
    fillMode: "both",
    keyframes: [
      { offset: 0, easing: "ease-in-out", values: { scale: 0.84, opacity: 0 } },
      {
        offset: 60,
        easing: "ease-in-out",
        values: { scale: 1.04, opacity: 1 },
      },
      { offset: 100, easing: "ease-in-out", values: { scale: 1, opacity: 1 } },
    ],
  },
  {
    name: "bounce",
    durationMs: 700,
    delayMs: 0,
    iterationCount: 1,
    direction: "normal",
    fillMode: "both",
    keyframes: [
      { offset: 0, easing: "ease-in-out", values: { y: 0 } },
      { offset: 30, easing: "ease-in-out", values: { y: -16 } },
      { offset: 60, easing: "ease-in-out", values: { y: 6 } },
      { offset: 100, easing: "ease-in-out", values: { y: 0 } },
    ],
  },
  {
    name: "drift",
    durationMs: 700,
    delayMs: 0,
    iterationCount: 1,
    direction: "normal",
    fillMode: "both",
    keyframes: [
      {
        offset: 0,
        easing: "ease-in-out",
        values: { x: 20, y: -14, rotation: -6, opacity: 0 },
      },
      {
        offset: 100,
        easing: "ease-in-out",
        values: { x: 0, y: 0, rotation: 0, opacity: 1 },
      },
    ],
  },
];

export const DEFAULT_SCENE_ANIMATION_NAMES: readonly string[] =
  DEFAULT_SCENE_ANIMATIONS.map((payload) => normalizeName(payload.name)).filter(
    (name) => name.length > 0,
  );

/**
 * Returns whether an animation name is already used by a default preset or a
 * catalog entry (comparison is trim + case-insensitive).
 *
 * @param name - Candidate animation name.
 * @param catalog - Scene behavior catalog to scan.
 * @param options.excludeEntryId - Catalog entry id to ignore (e.g. while renaming).
 */
export function isSceneAnimationNameTaken(
  name: string,
  catalog: SceneBehaviorCatalog | undefined,
  options?: { excludeEntryId?: string },
): boolean {
  const normalized = normalizeName(name).toLocaleLowerCase();
  if (!normalized) return false;
  if (
    DEFAULT_SCENE_ANIMATION_NAMES.some(
      (defaultName) => defaultName.toLocaleLowerCase() === normalized,
    )
  ) {
    return true;
  }
  const excludeEntryId = options?.excludeEntryId;
  return (catalog?.animations ?? []).some(
    (entry) =>
      entry.id !== excludeEntryId &&
      normalizeName(entry.name).toLocaleLowerCase() === normalized,
  );
}

function getDefaultSceneAnimations(): SceneAnimationExtraction {
  const byName: Record<string, BehaviorAnimationPayload> = {};
  const validNames: string[] = [];
  for (const payload of DEFAULT_SCENE_ANIMATIONS) {
    const name = normalizeName(payload.name);
    if (!name) continue;
    byName[name.toLocaleLowerCase()] = payload;
    validNames.push(name);
  }
  return { byName, validNames };
}

function getOutgoingNodeIdByPort(
  connections: BehaviorConnection[],
  fromNodeId: string,
  fromPortId: string,
): string[] {
  return connections
    .filter((connection) => {
      return (
        connection.fromNodeId === fromNodeId &&
        connection.fromPortId === fromPortId
      );
    })
    .map((connection) => connection.toNodeId);
}

function getBehaviorNodeById(
  nodes: BehaviorNode[],
  id: string,
): BehaviorNode | undefined {
  return nodes.find((node) => node.id === id);
}

function normalizeName(raw: unknown): string {
  return String(raw ?? "").trim();
}

export interface SceneAnimationGraphLike {
  nodes: BehaviorNode[];
  connections: BehaviorConnection[];
}

/**
 * Returns whether the graph already contains an `animBegin` or `animEnd` node.
 */
export function sceneAnimationGraphHasTerminalKind(
  graph: SceneAnimationGraphLike | undefined,
  kind: "animBegin" | "animEnd",
): boolean {
  if (!graph) return false;
  return graph.nodes.some(
    (node) => node.type === "action" && node.kind === kind,
  );
}

/**
 * Validates the structural animation chain:
 * exactly one `animBegin`, one `animEnd`, and `animBegin -> animKeyframe+ -> animEnd`
 * via single `out` flow links.
 */
export function isValidSceneAnimationGraphStructure(
  graph: SceneAnimationGraphLike | undefined,
): boolean {
  if (!graph || graph.nodes.length === 0) return false;

  const beginNodes = graph.nodes.filter(
    (node) => node.type === "action" && node.kind === "animBegin",
  );
  const endNodes = graph.nodes.filter(
    (node) => node.type === "action" && node.kind === "animEnd",
  );
  if (beginNodes.length !== 1 || endNodes.length !== 1) return false;

  const begin = beginNodes[0]!;
  const end = endNodes[0]!;
  const seen = new Set<string>([begin.id]);
  let currentIds = getOutgoingNodeIdByPort(graph.connections, begin.id, "out");
  let keyframeCount = 0;

  while (currentIds.length === 1) {
    const currentId = currentIds[0]!;
    if (seen.has(currentId)) return false;
    seen.add(currentId);

    const current = getBehaviorNodeById(graph.nodes, currentId);
    if (!current || current.type !== "action") return false;

    if (current.kind === "animKeyframe") {
      keyframeCount += 1;
      currentIds = getOutgoingNodeIdByPort(
        graph.connections,
        current.id,
        "out",
      );
      continue;
    }

    if (current.kind === "animEnd" && current.id === end.id) {
      return keyframeCount >= 1;
    }

    return false;
  }

  return false;
}

/**
 * Returns the `durationMs` port value from `animBegin` when the graph structure is valid.
 */
export function getValidSceneAnimationDurationMs(
  graph: SceneAnimationGraphLike | undefined,
): number | null {
  if (!isValidSceneAnimationGraphStructure(graph)) return null;

  const begin = graph!.nodes.find(
    (node) => node.type === "action" && node.kind === "animBegin",
  );
  if (!begin) return null;

  const durationPort = begin.ports.find((port) => port.id === "durationMs");
  const delayPort = begin.ports.find((port) => port.id === "delayMs");
  const iterationCountPort = begin.ports.find(
    (port) => port.id === "iterationCount",
  );
  const durationValue = Number(durationPort?.value);
  const delayValue = Number(delayPort?.value);
  const iterationCountValue = Number(iterationCountPort?.value);
  return Math.round(delayValue + durationValue * iterationCountValue);
}

/**
 * Keeps only catalog animation entries whose graph is structurally valid.
 */
export function filterValidSceneAnimationEntries<
  T extends { graph?: SceneAnimationGraphLike },
>(entries: readonly T[]): T[] {
  return entries.filter((entry) =>
    isValidSceneAnimationGraphStructure(entry.graph),
  );
}

/**
 * Extracts a single animation payload from a catalog entry's graph.
 * The animation name comes from the entry, not from the animBegin node.
 */
export function extractAnimationFromEntry(
  entry: SceneAnimationEntry,
  data: InjectedData,
  currentNodeContext?: import("./types.js").CurrentNodeContext,
): BehaviorAnimationPayload | null {
  const graph = entry.graph;
  if (!graph || graph.nodes.length === 0) return null;

  const runtime = BehaviorRuntime.build(graph);
  if (!isValidSceneAnimationGraphStructure(graph)) return null;

  const begin = graph.nodes.find(
    (node) => node.type === "action" && node.kind === "animBegin",
  );
  if (!begin) return null;
  const runtimeBegin = runtime.getNode(begin.id);
  if (!runtimeBegin) return null;

  const context: ExecutionContext = runtime.createContext(
    Date.now(),
    data,
    undefined,
    currentNodeContext,
  );

  const seen = new Set<string>([begin.id]);
  let currentIds = getOutgoingNodeIdByPort(graph.connections, begin.id, "out");
  const keyframes: BehaviorAnimationPayload["keyframes"] = [];
  let hasEnd = false;

  while (currentIds.length === 1) {
    const currentId = currentIds[0]!;
    if (seen.has(currentId)) break;
    seen.add(currentId);
    const current = getBehaviorNodeById(graph.nodes, currentId);
    const currentRuntime = runtime.getNode(currentId);
    if (!current || !currentRuntime || current.type !== "action") break;

    if (current.kind === "animKeyframe") {
      const keyframeVisiting = new Set<string>();
      const offset = Math.max(
        0,
        Math.min(
          100,
          toFiniteNumber(
            currentRuntime.getInput("offset", keyframeVisiting, context),
            0,
          ),
        ),
      );
      const easing = current.ports.some(
        (port) => port.id === "easing" && port.direction === "input",
      )
        ? normalizeAnimationEasingValue(
            currentRuntime.getInput("easing", keyframeVisiting, context),
          )
        : normalizeAnimationEasingValue(undefined);
      const values: Partial<Record<AnimatableProperty, number>> = {};
      for (const prop of ANIMATABLE_PROPERTIES) {
        if (!animKeyframeHasPropertyPort(current, prop)) continue;
        const value = currentRuntime.getInput(prop, keyframeVisiting, context);
        values[prop] = toFiniteNumber(value, 0);
      }
      keyframes.push({ offset, easing, values });
      currentIds = getOutgoingNodeIdByPort(
        graph.connections,
        current.id,
        "out",
      );
      continue;
    }

    if (current.kind === "animEnd") {
      hasEnd = true;
    }
    break;
  }

  if (!hasEnd || keyframes.length === 0) return null;

  const beginVisiting = new Set<string>();
  return {
    name: entry.name,
    durationMs: Math.max(
      0,
      toFiniteNumber(
        runtimeBegin.getInput("durationMs", beginVisiting, context),
        1000,
      ),
    ),
    delayMs: Math.max(
      0,
      toFiniteNumber(
        runtimeBegin.getInput("delayMs", beginVisiting, context),
        0,
      ),
    ),
    iterationCount: Math.max(
      1,
      Math.floor(
        toFiniteNumber(
          runtimeBegin.getInput("iterationCount", beginVisiting, context),
          1,
        ),
      ),
    ),
    direction: coerceCssAnimationDirection(
      runtimeBegin.getInput("direction", beginVisiting, context),
    ),
    fillMode: coerceCssAnimationFillMode(
      runtimeBegin.getInput("fillMode", beginVisiting, context),
    ),
    keyframes,
  };
}

/**
 * Extracts valid named animations from the scene catalog.
 * Custom entries override default presets when names collide (case-insensitive).
 */
export function extractSceneAnimations(
  catalog: SceneBehaviorCatalog | undefined,
  data: InjectedData,
  currentNodeContext?: import("./types.js").CurrentNodeContext,
): SceneAnimationExtraction {
  const defaults = getDefaultSceneAnimations();
  if (!catalog || catalog.animations.length === 0) {
    return defaults;
  }

  const byName: Record<string, BehaviorAnimationPayload> = {
    ...defaults.byName,
  };
  const validNamesSet = new Set<string>(defaults.validNames);

  for (const entry of catalog.animations) {
    const name = normalizeName(entry.name);
    if (!name) continue;
    const payload = extractAnimationFromEntry(entry, data, currentNodeContext);
    if (!payload) continue;
    const normalized = name.toLocaleLowerCase();
    byName[normalized] = payload;
    validNamesSet.add(name);
  }

  return {
    byName,
    validNames: [...validNamesSet].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    ),
  };
}
